import { useState, useEffect } from 'react';
import { Mail, Linkedin, Github, Send, ChevronRight } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { Reveal } from '../hooks/useReveal.tsx';
import { MeshText } from './MeshText';

const getEnvVariable = (key: string, defaultValue: string = '') => {
  return import.meta.env[key] || defaultValue;
};

export function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    emailjs.init(getEnvVariable('VITE_EMAILJS_PUBLIC_KEY'));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const templateParams = {
        to_name: 'Paras',
        from_name: formData.name,
        from_email: formData.email,
        message: formData.message,
        reply_to: formData.email,
      };

      await emailjs.send(
        getEnvVariable('VITE_EMAILJS_SERVICE_ID'),
        getEnvVariable('VITE_EMAILJS_TEMPLATE_ID'),
        templateParams,
        getEnvVariable('VITE_EMAILJS_PUBLIC_KEY')
      );

      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const socialLinks = [
    {
      icon: Mail,
      label: 'Email',
      href: 'mailto:kanojiaparas582@gmail.com',
      text: 'kanojiaparas582@gmail.com',
    },
    {
      icon: Linkedin,
      label: 'LinkedIn',
      href: 'https://www.linkedin.com/in/dtu-paras-kanojia/',
      text: 'Connect on LinkedIn',
    },
    {
      icon: Github,
      label: 'GitHub',
      href: 'https://github.com/Paras1209',
      text: 'View GitHub Profile',
    },
  ];

  return (
    <section
      id="contact"
      className="flex flex-col px-5 sm:px-8 md:px-12 py-16 sm:py-20"
    >
      {/* Header */}
      <div className="flex flex-col gap-8 sm:flex-row sm:justify-between mb-12">
        <Reveal delay={120}>
          <div className="accent-badge px-3 py-1.5 rounded-r-md inline-block">
            <span className="mono-label-sm text-white/90">Get in Touch</span>
          </div>
        </Reveal>

        <Reveal delay={220} className="max-w-sm sm:text-right">
          <p className="text-lg sm:text-xl leading-relaxed text-white text-shadow">
            Have a project in mind or want to collaborate? I'd love to hear from you.
          </p>
        </Reveal>
      </div>

      {/* Main Content */}
      <div className="flex flex-col">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-start">
          {/* Left - Contact Info */}
          <div>
            <Reveal delay={180}>
              <div className="flex flex-col gap-0 mb-6">
                <div className="h-[50px] sm:h-[60px] lg:h-[75px] w-[280px] sm:w-[340px] lg:w-[420px]">
                  <MeshText
                    text="Let's create"
                    color="#ffffff"
                    fontFamily="Inter"
                    fontWeight={500}
                    fontSize={55}
                    colorSplit={true}
                    splitColors={['#61dafb', '#ff40c0']}
                    force={18}
                  />
                </div>
                <div className="h-[50px] sm:h-[60px] lg:h-[75px] w-[380px] sm:w-[460px] lg:w-[560px]">
                  <MeshText
                    text="something great."
                    color="#ffffff"
                    fontFamily="Inter"
                    fontWeight={500}
                    fontSize={55}
                    colorSplit={true}
                    splitColors={['#40ff80', '#61dafb']}
                    force={18}
                  />
                </div>
              </div>
            </Reveal>

            <Reveal delay={280}>
              <p className="text-white/70 mb-8 max-w-md">
                Whether you have a question, want to discuss a project, or just want to say hi, 
                feel free to reach out. I'm always open to new opportunities and collaborations.
              </p>
            </Reveal>

            {/* Social Links */}
            <div className="space-y-4">
              {socialLinks.map((link, index) => (
                <Reveal key={link.label} delay={350 + index * 80}>
                  <a
                    href={link.href}
                    target={link.href.startsWith('mailto') ? undefined : '_blank'}
                    rel={link.href.startsWith('mailto') ? undefined : 'noopener noreferrer'}
                    className="flex items-center gap-4 text-white/70 hover:text-white transition-colors group"
                  >
                    <div className="p-2 rounded-lg bg-white/10 group-hover:bg-white/15 transition-colors">
                      <link.icon size={18} />
                    </div>
                    <span className="text-sm">{link.text}</span>
                    <ChevronRight
                      size={14}
                      className="opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all"
                    />
                  </a>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Right - Contact Form */}
          <Reveal delay={400}>
            <form
              onSubmit={handleSubmit}
              className="rounded-2xl border border-white/15 bg-white/10 backdrop-blur-md p-6 sm:p-8"
            >
              <div className="space-y-5">
                {/* Name Field */}
                <div>
                  <div className="h-[16px] w-[60px] mb-2">
                    <MeshText
                      text="Name"
                      color="rgba(255,255,255,0.6)"
                      fontFamily="monospace"
                      fontWeight={400}
                      fontSize={11}
                      colorSplit={true}
                      splitColors={['#61dafb', '#40ff80']}
                      force={8}
                    />
                  </div>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-lg text-white placeholder-white/40 focus:border-white/30 transition-colors"
                    placeholder="Your name"
                  />
                </div>

                {/* Email Field */}
                <div>
                  <div className="h-[16px] w-[60px] mb-2">
                    <MeshText
                      text="Email"
                      color="rgba(255,255,255,0.6)"
                      fontFamily="monospace"
                      fontWeight={400}
                      fontSize={11}
                      colorSplit={true}
                      splitColors={['#61dafb', '#40ff80']}
                      force={8}
                    />
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-lg text-white placeholder-white/40 focus:border-white/30 transition-colors"
                    placeholder="your.email@example.com"
                  />
                </div>

                {/* Message Field */}
                <div>
                  <div className="h-[16px] w-[80px] mb-2">
                    <MeshText
                      text="Message"
                      color="rgba(255,255,255,0.6)"
                      fontFamily="monospace"
                      fontWeight={400}
                      fontSize={11}
                      colorSplit={true}
                      splitColors={['#61dafb', '#40ff80']}
                      force={8}
                    />
                  </div>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-lg text-white placeholder-white/40 focus:border-white/30 transition-colors resize-none"
                    placeholder="Tell me about your project or idea..."
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full btn-primary rounded-lg px-6 py-3 text-sm font-medium inline-flex items-center justify-center gap-2 ${
                    isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send size={16} />
                    </>
                  )}
                </button>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <p className="text-center text-sm text-green-400">
                    Message sent successfully! I'll get back to you soon.
                  </p>
                )}
                {submitStatus === 'error' && (
                  <p className="text-center text-sm text-red-400">
                    Failed to send message. Please try again or email me directly.
                  </p>
                )}
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
